import { CommentList, ProjectIntakeApprovalStatus } from "@/components";
import { DynamicFormContent } from "@/components/DynamicForm/DynamicFormContent";
import { DynamicFormProvider } from "@/components/DynamicForm/DynamicFormProvider";
import { isUserNotificationUser } from "@/components/ProjectIntakeEditor/helpers";
import { ProjectIntakeStatusTracker } from "@/components/ProjectIntakeStatusTracker";
import * as GlobalS from "@/components/styled";
import SpinLoader from "@/containers/Layout/MainLayout/SpinLoader";
import {
  ProjectHeader,
  ProjectLeaderFields,
  SkuCard,
} from "@/containers/Project/components";
import { CommentTarget, FormType, PROJECT_STATUS } from "@/enums";
import { ProjectHeaderElements } from "@/enums/ProjectHeaderElements";
import { useMobileLayout } from "@/hooks/useAppMedia";
import { useFormPropertyProvider, useUserContext } from "@/providers";
import { useGetCommentsQuery, useGetFlattenedApproversQuery } from "@/services";
import { Badge, Col } from "antd";
import { bool, func, node, object, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import * as S from "./styled";

const ProjectIntakeEditor = ({
  formRef = null,
  intakeData = null,
  formDisabled = false,
  canChangeFields = false,
  showComments = true,
  placeholderId = null,
  leftActions,
  rightActions,
  onFinish,
  isSubmitting,
  isUserAdminOrProjectLeader = false,
  isNewSubmission = false,
  canToggleSecure = false,
}) => {
  // Update tabs to use string literals on conversion to TS
  // https://dev.azure.com/MilwaukeeToolAppDev/Viper/_git/Met.Viper.Web/pullrequest/21316?_a=files&path=%2Fsrc%2FWeb%2FClientApp%2Fsrc%2Fcomponents%2FProjectIntakeEditor%2FProjectIntakeEditor.js&discussionId=116240
  const [currentTab, setCurrentTab] = useState();
  const { projectId } = useParams();
  const intl = useIntl();
  const { getFormProperties } = useFormPropertyProvider();
  const { userToken } = useUserContext();
  const isMobileLayout = useMobileLayout();

  const [intakeForm, setIntakeForm] = useState([]);
  const [showDetails, setShowDetails] = useState(true);
  const [disableExpand, setDisableExpand] = useState(false);

  const isProjectEditable = () =>
    intakeData?.status === PROJECT_STATUS.intake.draft.id ||
    intakeData?.status === PROJECT_STATUS.intake.inApproval.id ||
    intakeData?.status === PROJECT_STATUS.project.active.id;

  const showProjectIntakeApprovalStatus = () =>
    isProjectEditable() ||
    intakeData?.status === PROJECT_STATUS.project.complete.id;

  const [inactiveHeaderElements, setInactiveHeaderElements] = useState([]);

  useEffect(() => {
    if (intakeData?.status) {
      setDisableExpand(intakeData.status === PROJECT_STATUS.intake.draft.id);

      if (intakeData.status === PROJECT_STATUS.intake.draft.id)
        setInactiveHeaderElements([
          ProjectHeaderElements.KPIS,
          ProjectHeaderElements.CTQ,
          ProjectHeaderElements.TEAMS,
        ]);
      else {
        setInactiveHeaderElements([
          ProjectHeaderElements.KPIS,
          ProjectHeaderElements.CTQ,
        ]);
      }
    } else {
      setDisableExpand(false);
    }
  }, [intakeData?.status]);

  const handleShowDetails = () => {
    setShowDetails((prev) => !prev);
  };

  const { data: allApprovers = [] } = useGetFlattenedApproversQuery(projectId, {
    skip: !projectId || !intakeData?.projectType,
  });

  const hasEditPermission = canChangeFields && isUserAdminOrProjectLeader;

  const { data: commentsResponse } = useGetCommentsQuery(
    {
      projectId: projectId,
      target: CommentTarget.IntakeProject,
      targetId: projectId,
    },
    {
      skip: !projectId,
    }
  );

  useEffect(() => {
    const form = getFormProperties(FormType.Intake);
    setIntakeForm(form);
  }, [getFormProperties]);

  const canEditIntakeComments = () => {
    const userId = userToken?.user?.id;
    if (userId) {
      const isNotificationUser = isUserNotificationUser(
        intakeData?.schedules,
        userToken?.azureActiveDirectoryGroupIds
      );

      if (
        (intakeData.projectLeaders &&
          Object.values(intakeData.projectLeaders).includes(userId)) ||
        intakeData.backupProjectLeader === userId ||
        intakeData.requestor === userId ||
        allApprovers?.includes(userId) ||
        isNotificationUser
      ) {
        return true;
      }
    }
    return false;
  };

  const getIndependentlyEditableFields = () => {
    const results = [];

    if (canToggleSecure) {
      results.push("SecureProject");
    }

    return results;
  };

  if (!intakeForm.length) return <SpinLoader />;

  const dynamicForm = () => (
    <DynamicFormProvider config={intakeForm} initialValues={intakeData}>
      <DynamicFormContent
        canChangeFields={canChangeFields}
        isSubmitting={isSubmitting}
        onFinish={(form) => onFinish(form, false)}
        formRef={formRef}
        formDisabled={formDisabled}
        lockEntireForm={!isProjectEditable()}
        placeholderId={placeholderId}
        extraInputs={[
          {
            propertyName: "stockKeepingUnits",
            title: intl.formatMessage({ id: "project.skuTitle" }),
            inputComponent: (
              <SkuCard
                key="1"
                readOnly={
                  !isProjectEditable() || (!isNewSubmission && !canChangeFields)
                }
                intakeId={intakeData?.id}
              />
            ),
            initialValues: intakeData?.stockKeepingUnits,
          },
        ]}
        extraCategories={[
          {
            title: intl.formatMessage({
              id: "regionalProjectLeaderCard.title",
            }),
            required: true,
            inputComponent: (
              <ProjectLeaderFields
                readOnly={
                  !isProjectEditable() ||
                  (!hasEditPermission && !isNewSubmission)
                }
                regionalProjectLeadersProp={{
                  propertyName: "projectLeaders",
                  initialValues: intakeData?.projectLeaders,
                }}
                formRef={formRef}
                backupProjectLeaderProp={{
                  propertyName: "backupProjectLeader",
                  initialValues: intakeData?.backupProjectLeader,
                }}
              />
            ),
          },
        ]}
        leftActions={leftActions}
        rightActions={rightActions}
        formHeader={
          <>
            {projectId && (
              <Col flex={1}>
                <ProjectHeader
                  showDetails={showDetails}
                  handleShowDetails={handleShowDetails}
                  mobile={isMobileLayout}
                  source={"intake"}
                  inActiveElements={inactiveHeaderElements}
                  expandButtonText={intl.formatMessage({
                    id: "projectIntake.viewApprovalStatus",
                  })}
                  disableExpand={disableExpand}
                />
                {!showProjectIntakeApprovalStatus() && showDetails && (
                  <ProjectIntakeStatusTracker
                    intakeData={intakeData}
                    statusLabel={intakeData.status}
                  />
                )}
                {showProjectIntakeApprovalStatus() &&
                  intakeData.workflowId &&
                  showDetails && (
                    <ProjectIntakeApprovalStatus
                      approvalWorkflowId={intakeData.workflowId}
                      workflowHistory={intakeData.workflowHistory}
                      projectStatusId={intakeData.status}
                    />
                  )}
              </Col>
            )}
          </>
        }
        independentlyEditableFields={getIndependentlyEditableFields()}
      />
    </DynamicFormProvider>
  );

  const tabs = [
    {
      key: "details",
      label: intl.formatMessage({ id: "general.details" }),
      children: dynamicForm(),
    },
    {
      key: "comments",
      label: (
        <>
          {intl.formatMessage({
            id: "general.comments",
          })}{" "}
          <Badge count={commentsResponse?.commentsCount} showZero />
        </>
      ),
      children: (
        <GlobalS.PageContentCard>
          <CommentList
            projectId={intakeData?.id}
            target={CommentTarget.IntakeProject}
            targetId={intakeData?.id}
            showTitle={true}
            editable={canEditIntakeComments()}
          />
        </GlobalS.PageContentCard>
      ),
    },
  ];

  const handleTabChange = (key) => {
    setCurrentTab(key);
  };

  return (
    <>
      {showComments ? (
        <S.ProjectIntakeTabs
          data-test-id="project-intake-editor-tabs"
          size="middle"
          activeKey={currentTab}
          defaultActiveKey={"details"}
          items={tabs}
          onChange={handleTabChange}
        />
      ) : (
        <>{dynamicForm()}</>
      )}
    </>
  );
};

ProjectIntakeEditor.propTypes = {
  isIntakeSaving: bool,
  formRef: object,
  intakeData: object,
  formDisabled: bool,
  canChangeFields: bool,
  showComments: bool,
  placeholderId: string,
  leftActions: node,
  rightActions: node,
  isSubmitting: bool,
  onFinish: func,
  isUserAdminOrProjectLeader: bool,
  isNewSubmission: bool,
  canToggleSecure: bool,
};

export default ProjectIntakeEditor;
